import Vue from 'vue'
import VueRouter from 'vue-router'

// Routes
import { canNavigate } from '@/libs/acl/routeProtection'
import {
  isUserLoggedIn,
  getUserData,
  getHomeRouteForLoggedInUser,
  clearTwoFaSetupSession,
  needsAdminTwoFactor,
  logoutLocalSession,
  isTwoFaSetupSessionActive,
  getTwoFaSetupSession,
} from '@/auth/utils'
import twoFactorApi from '@/api/twoFactor'
import apps from './routes/apps'
import dashboard from './routes/dashboard'
import uiElements from './routes/ui-elements/index'
import pages from './routes/pages'
import chartsMaps from './routes/charts-maps'
import formsTable from './routes/forms-tables'
import others from './routes/others'
import partner from './routes/partner'

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    { path: '/', redirect: { name: 'manage-lotto-status' } },
    ...apps,
    ...dashboard,
    ...pages,
    ...chartsMaps,
    ...formsTable,
    ...uiElements,
    ...others,
    ...partner,
    {
      path: '*',
      redirect: 'manage-lotto-status',
    },
  ],
})

// Pages reachable during 2FA setup without full ACL
const aclBypassRoutes = ['security-2fa-required', 'security-2fa']

const ensureAdminTwoFactor = async () => {
  if (!isUserLoggedIn()) return null

  const userData = getUserData()
  if (!userData || !needsAdminTwoFactor(userData)) return null
  if (sessionStorage.getItem('adminTwoFaOk') === '1') return null

  try {
    const { data } = await twoFactorApi.getStatus()
    if (data.enabled) {
      sessionStorage.setItem('adminTwoFaOk', '1')
      localStorage.setItem('userData', JSON.stringify({ ...userData, twoFactorEnabled: true }))
      return null
    }

    logoutLocalSession()
    return { name: 'auth-login', query: { setup2fa: '1' } }
  } catch (e) {
    const status = e?.response?.status
    // Only force re-login when auth confirms session is invalid or 2FA is required
    if (status === 401) {
      logoutLocalSession()
      return { name: 'auth-login' }
    }
    // Network/server error — do not logout
    return null
  }
}

router.beforeEach(async (to, _, next) => {
  const isLoggedIn = isUserLoggedIn()

  // Logged-in users should never stay on the pre-login 2FA setup flow
  if (isLoggedIn) {
    clearTwoFaSetupSession()
  } else if (isTwoFaSetupSessionActive() && to.name !== 'security-2fa-required') {
    return next({ name: 'security-2fa-required' })
  }

  if (aclBypassRoutes.includes(to.name)) {
    return next()
  }

  if (to.name === 'auth-login') {
    const twoFaRedirect = await ensureAdminTwoFactor()
    if (twoFaRedirect) return next(twoFaRedirect)
    if (isLoggedIn) {
      const userData = getUserData()
      return next(getHomeRouteForLoggedInUser(userData ? userData.role : null, userData ? userData.path : null))
    }
    return next()
  }

  const twoFaRedirect = await ensureAdminTwoFactor()
  if (twoFaRedirect) {
    return next(twoFaRedirect)
  }

  if (!canNavigate(to)) {
    if (!isLoggedIn) return next({ name: 'auth-login' })

    if (to.name === 'manage-lotto-status') {
      return next()
    }
    return next({ name: 'manage-lotto-status' })
  }

  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    const userData = getUserData()
    return next(getHomeRouteForLoggedInUser(userData ? userData.role : null, userData ? userData.path : null))
  }

  return next()
})

router.afterEach(() => {
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

export default router
